<template>
	<div class="bg-white grow flex flex-col overflow-y-auto scrollbar-hide">
		<FormKit
			:id="formId"
			v-slot="{ node }"
			ref="form"
			v-model="vModel"
			type="form"
			form-class="flex flex-col grow relative"
			:disabled="disableForm"
			:actions="false"
			:data="data"
			v-on="events.form"
			@submit-invalid="currentLoadingButton = null"
		>
			<div class="sticky top-0 w-full p-5 z-20 flex bg-primary">
				<slot name="header">
					<div
						class="flex flex-row flex-wrap justify-between items-start gap-5 w-full"
					>
						<div>
							<slot
								name="header-title"
								v-bind="{ resourceTitle: resource?.title }"
							>
								<h1 class="text-lg font-medium">
									{{ customTitle ?? resource?.title }}
									{{ type === "Create" ? "aanmaken" : "bewerken" }}
								</h1>
							</slot>
							<AppAlert type="error"
							          data-testid="form-validation-errors"
							          v-if="Object.keys(node.context.messages).length > 0">
								De volgende fouten zijn opgetreden:
								<div class="mt-1 max-h-24 2xl:max-h-36 w-full overflow-y-auto">
									<FormKitMessages :sections-schema="{
                    messages: {
                      $el: 'ul',
                      attrs: {
                        class: 'list-disc list-inside'
                      }
                    },
                message: {
                  $el: 'li',
                  attrs: {
                    class: ''
                  },
                }
              }"/>
								</div>
							</AppAlert>
							<slot name="header-message"/>
						</div>
						<div class="flex items-center gap-1 text-black">
							<slot name="before-actions"
							      v-bind="{ node, submit: () => submit(vModel), hasActiveSubmitRequest, setLoadingButton, currentLoadingButton }"
							/>
							<slot
								name="header-actions"
								v-bind="{ node, submit: () => submit(vModel), submitWithMode, hasActiveSubmitRequest, setLoadingButton, currentLoadingButton }"
							>
								<div role="group">
									<AppButton
										v-if="!hideSave"
										id="formSave"
										v-tooltip="'Opslaan (Ctrl+S)'"
										type="submit"
										:icon="hasActiveSubmitRequest && currentLoadingButton == 'formSave' ? 'fa-spinner' : 'fa-floppy-disk'"
										:spin="currentLoadingButton == 'formSave'"
										:disabled="!isReady || disableSubmit || hasActiveSubmitRequest"
										@click.prevent="submitWithMode('save')"
									>
										Opslaan
									</AppButton>
									<AppButton
										v-if="!hideSaveAndNew"
										id="formSaveAndNew"
										v-tooltip="'Opslaan en nieuw (Ctrl+Shift+S)'"
										type="submit"
										:icon="hasActiveSubmitRequest && currentLoadingButton == 'formSaveAndNew' ? 'fa-spinner' : 'fa-floppy-disk-circle-arrow-right'"
										:spin="currentLoadingButton == 'formSaveAndNew'"
										:disabled="!isReady || disableSubmit || hasActiveSubmitRequest"
										@click.prevent="submitWithMode('save-and-new')"
									>
										en nog een aanmaken
									</AppButton>
									<AppButton
										v-if="showRowCreateSave"
										id="formSaveAndNewRow"
										v-tooltip="`Opslaan en nieuwe ${resource.title.toLowerCase() } regel aanmaken`"
										type="submit"
										:icon="hasActiveSubmitRequest && currentLoadingButton == 'formSaveAndNewRow' ? 'fa-spinner' : 'fa-floppy-disk-pen'"
										:spin="currentLoadingButton == 'formSaveAndNewRow'"
										:disabled="!isReady || disableSubmit || hasActiveSubmitRequest"
										@click.prevent="submitWithMode('save-and-row')"
									>
										en naar regel aanmaken
									</AppButton>
									<AppButton
										v-if="!hideSaveAndClose"
										id="formSaveAndClose"
										v-tooltip="'Opslaan en sluiten (Ctrl+S+F4)'"
										type="submit"
										:icon="hasActiveSubmitRequest && currentLoadingButton == 'formSaveAndClose' ? 'fa-spinner' : 'fa-floppy-disk-circle-xmark'"
										:spin="currentLoadingButton == 'formSaveAndClose'"
										:disabled="!isReady || disableSubmit || hasActiveSubmitRequest"
										@click.prevent="submitWithMode('save-and-close')"
									>
										en sluiten
									</AppButton>
								</div>
							</slot>
							<slot name="after-actions"
							      v-bind="{ node, submit: () => submit(vModel), submitWithMode, hasActiveSubmitRequest, setLoadingButton, currentLoadingButton }"
							/>
						</div>
					</div>
				</slot>
			</div>
			<div
				v-if="formArrayDefinition && Object.values(formObjectDefinition).length > 0"
				class="flex flex-row flex-wrap h-full"
			>
				<slot
					v-bind="{
            formNode,
            formArrayDefinition,
            schema: formObjectDefinition as any as RequiredNonNullableObject<T>
          }"
				>
					<!--
						Three ways to lay a form out, in order of precedence: a caller-provided
						#default slot (this fallback is skipped entirely), a layout defined on the
						backend, or the flat list of fields we have always rendered.
					-->
					<!--
						The wrapper is load-bearing: AppFormLayout renders a fragment (one node per
						top-level layout entry), so it has no root element to receive a class. Left
						to itself, its nodes become flex items of the flex-row container above and
						sit side by side. Padding is deliberately not set here — a layout owns its
						own spacing, so a tabbed form can sit flush.
					-->
					<div
						v-if="formLayout?.length"
						class="flex flex-col grow h-full"
					>
						<AppFormLayout v-bind="{ nodes: formLayout, schema: formObjectDefinition, model: vModel }"/>
					</div>
					<div
						v-else
						class="flex flex-col flex-wrap gap-5 p-5"
					>
						<template
							v-for="inputSchema in formArrayDefinition"
							:key="inputSchema.name"
						>
							<slot
								:name="inputSchema.name"
								v-bind="{ inputSchema }"
							>
								<FormKitSchema
									:schema="inputSchema"
									outer-class="col-span-full"
								/>
							</slot>
						</template>
					</div>
				</slot>
			</div>
			<AppDataGridLookupPromise ref="gridLookup"/>
		</FormKit>
	</div>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import { FormKitNode } from "@formkit/core";
import { FormKitMessages } from "@formkit/vue";
import { AxiosError, AxiosResponse } from "axios";
import { reduce } from "lodash";
import { ModelRef, ref } from "vue";
import AppDataGridLookupPromise from "~/components/AppDataGridLookupPromise.vue";
import { translate } from "~/plugins/I18n.ts";
import { useApiResources } from "~/stores/ApiResources";
import { useAppTabs } from "~/stores/useAppTabs";
import { RequiredNonNullableObject } from "~/types";
import { getFormName, useForm } from "~/utils/Forms/useForm";
import { isFormSubmissionError } from "~/utils/zod/formSubmissionError";
import { onFormMounted, onInputMounted } from "~utils/Forms/FormUIUtils.ts";
import { useGridLookup } from "~utils/Forms/useGridLookup.ts";
import { refreshGridsOfResource } from "~utils/Grids/RefreshGrid.ts";
import { processConfirmation } from "~utils/ConfirmationHelper.ts";

const props = withDefaults(
	defineProps<{
		// Used to fetch the form definition
		resourceKey: string;
		type: "Edit" | "Create";
		resourceId?: string;
		resourceUrl?: string;
		modelValue?: T;
		formName?: string;
		formId?: string;
		// Custom submit url
		submitUrl?: string;
		redirectUrl?: string;
		submitText?: string;
		submitIcon?: string;
		submitTooltip?: string;
		customTitle?: string;
		closeOnSubmitSuccess?: boolean;
		disableSubmit?: boolean;
		hideSave?: boolean;
		hideSaveAndNew?: boolean;
		hideSaveAndClose?: boolean;
		showRowCreateSave?: boolean;
		disableForm?: boolean;
		onBeforeSubmit?: (data: T, node: FormKitNode) => Promise<T> | T;
	}>(),
	{
		disableForm: false,
		hideSave: false,
		hideSaveAndNew: false,
		hideSaveAndClose: false,
		showRowCreateSave: false,
		closeOnSubmitSuccess: false,
		onBeforeSubmit: async (data: T) => data,
	},
);
const closeOnSubmitSuccess = ref(props.closeOnSubmitSuccess);
const { disableSubmit } = toRefs(props);

// Unique per instance. Every open tab stays in the DOM (App.vue uses v-show), and FormKit resolves a
// programmatic submit through document.getElementById(id) — first match in document order wins. As a
// withDefaults() default this expression ran once when the module loaded, so every form in the app shared
// one id and Save submitted whichever form mounted first instead of the visible one.
const instanceFormId = `form-${Math.random().toString(36).slice(2)}`;
const formId = computed(() => props.formId ?? instanceFormId);
const hasActiveSubmitRequest = ref(false);
const currentLoadingButton = ref<null | string>(null);

const defaultSaveMenuItems = computed(() => {
	const items = [
		{
			disabled: disableSubmit.value,
			text: props.submitText ?? "Opslaan",
			icon: props.submitIcon ?? "fa-save",
			id: "save",
			action: () => submitWithMode("save"),
		},
	];

	if (props.hideSaveAndNew == false) {
		items.push({
			disabled: disableSubmit.value,
			text: "Opslaan en nieuw",
			icon: "fa-floppy-disk-circle-arrow-right",
			id: "save-and-new",
			action: () => submitWithMode("save-and-new"),
		});
	}

	return items;
});

const { error, success } = useToast();
const { closeCurrentTab, activeTab } = useAppTabs();
const { replace } = useRouter();
const { findByName } = useApiResources();
const saveAndNew = ref(false);
const saveAndCreateRow = ref(false);
const dirty = ref(false);
const data = reactive({});
// `useModel` stores an assigned object verbatim, so a <FormPage> rendered *without* v-model —
// default.create.vue / default.update.vue — held a plain, untracked object: FormKit hands back a
// `shallowClone` of its value on every commit, and nothing ever wrapped it. Writes into it were
// then invisible both to AppSchemaInput's own `inputValue` computed (which stayed cached, so a
// re-render put the stale value straight back into the DOM) and to FormKit's deep watcher on
// `modelValue` (so the typed value never reached the submitted payload). A parent binding a `ref`
// got this wrapping for free; wrap it here so the model's reactivity no longer depends on that.
const vModel = defineModel<T>({
	get: (value: T) => (value != null && typeof value === "object" ? (reactive(value as object) as T) : value),
}) as ModelRef<T>;
const resource = findByName(props.resourceKey);

const BUTTON_IDS = {
	save: "formSave",
	"save-and-new": "formSaveAndNew",
	"save-and-row": "formSaveAndNewRow",
	"save-and-close": "formSaveAndClose",
} as const;
type SaveMode = keyof typeof BUTTON_IDS;

function setMode(mode: SaveMode) {
	saveAndNew.value = mode === "save-and-new";
	saveAndCreateRow.value = mode === "save-and-row";
	closeOnSubmitSuccess.value = mode === "save-and-close";
	currentLoadingButton.value = BUTTON_IDS[mode];
}

/**
 * @param loadingButtonId Overrides which button shows the spinner. A slotted button (see the
 * `after-actions` slot) drives one of the standard modes but is not one of the standard buttons, so
 * without this `setMode` would spin whichever built-in button happens to match the mode.
 */
function submitWithMode(mode: SaveMode, loadingButtonId?: string) {
	if (!isReady.value || disableSubmit.value || hasActiveSubmitRequest.value) return;
	setMode(mode);

	if (loadingButtonId != null) {
		currentLoadingButton.value = loadingButtonId;
	}

	// FormKit programmatic submit → events.form.submit → submit()
	formNode.submit();
}

const keys = useMagicKeys({
	onEventFired: (e) => {
		if (e.key === "s" && e.ctrlKey) {
			e.preventDefault();
			setMode(e.shiftKey ? "save-and-new" : "save");
		}
	},
	passive: false,
});

const watchKeys = whenever(keys.ctrl_s, () => {
	formNode.submit();
});

onActivated(() => {
	watchKeys.resume();
});

onDeactivated(() => {
	watchKeys.pause();
});

const formName = props.formName ?? getFormName(resource.name, props.type);
const emit = defineEmits<(e: "submit-success", data: T) => void>();

const { isReady, formArrayDefinition, formObjectDefinition, formLayout, submitForm, onSubmitError, onSubmitSuccess } =
	useForm<T>(formName, resource, props.resourceId, vModel);

onSubmitError((e) => {
	currentLoadingButton.value = null;
	// TODO: Remove ZOD
	const submissionError = isFormSubmissionError(e);
	if (submissionError.success) {
		const { data } = submissionError;
		if (e.status == 428) {
			processConfirmation(e.parameters.errors, vModel.value, submit);
			return;
		}

		data.violations?.forEach((violation) => {
			formNode.emit("field-constraint-violation", violation);
		});

		// On the node itself rather than setErrors(id): that resolves through the global registry, which
		// keeps the most recently registered node per id — a different form than the one being submitted.
		formNode.setErrors(
			data.violations != null ? data.violations?.map((it) => it.message) : data.message,
			reduce(
				data.violations || [],
				(obj, v) => {
					if (!(v.field in obj)) {
						obj[v.field] = [];
					}

					obj[v.field].push(v.message);
					return obj;
				},
				{},
			),
		);
	} else {
		const responseData = (e as AxiosError).response.data;
		if (responseData != undefined && "localizedMessage" in responseData) {
			error(responseData.localizedMessage);
		} else {
			console.log(e);
			error("Er is iets onbekend misgegaan bij het opslaan van het formulier.");
		}
	}

	// Genuine failure (a 428 confirmation returns earlier and re-submits): clear any
	// pending save-mode so a subsequent plain save doesn't inherit "save-and-new" etc.
	saveAndNew.value = false;
	saveAndCreateRow.value = false;
	closeOnSubmitSuccess.value = props.closeOnSubmitSuccess;
});

onSubmitSuccess(async (res: AxiosResponse) => {
	let id = null;
	if (res.status == 201 && "id" in res.data) {
		id = res.data.id;
	}

	emit("submit-success", vModel.value);

	if (saveAndNew.value) {
		// On a create form, reopen the same path so the context it was opened with (query params
		// such as purchaseOrderRowId) carries over; it comes from the tab system's own record
		// rather than the (tab-agnostic) vue-router location, and the tab router appends its own
		// `_t` marker on every open, so strip previous ones to keep them from accumulating.
		// On an edit form that path is `/<resource>/update/<id>`, which would just reopen the same
		// entity — fall through to the resource's create route instead.
		const reopenPath = props.type === "Create" ? activeTab.value?.fullPath?.replace(/[?&]_t=[^&]*/g, "") : undefined;

		await closeCurrentTab(false);

		if (reopenPath) {
			await replace(reopenPath);
		} else {
			let url = `${props.redirectUrl ?? `/${resource.name}`}/create`;
			if (props.resourceUrl) {
				url += `?resourceUrl=${props.resourceUrl}`;
			}
			await replace(url);
		}
	} else if (closeOnSubmitSuccess.value) {
		await closeCurrentTab();
	} else if (saveAndCreateRow.value) {
		await closeCurrentTab();
		const resourceName = resource.name.replace(/order/, "order-row");
		const url = `/${resourceName}/create?resourceUrl=/api/${resource.name}/${id ?? props.resourceId}/rows`;
		await replace(props.redirectUrl ?? url);
	} else if (props.type == "Create" && id != null) {
		await closeCurrentTab();
		let url = props.redirectUrl ?? `/${resource.name}`;
		url += `/update/${id}`;
		if (props.resourceUrl) {
			url += `?resourceUrl=${props.resourceUrl}`;
		}
		await replace(url);
	} else if (props.type == "Edit") {
		// Edit page, close tab then open new create tab
		await closeCurrentTab();
		let url = props.redirectUrl ?? `/${resource.name}`;
		url += `/update/${props.resourceId}`;
		if (props.resourceUrl) {
			url += `?resourceUrl=${props.resourceUrl}`;
		}
		await replace(url);
	}

	success(translate(`entities.${resource.name}`) + " opgeslagen.");

	refreshGridsOfResource(resource.name, id);
});

let formNode: FormKitNode;
const formNodeRef = ref<FormKitNode>(formNode);
const gridLookup = ref<InstanceType<typeof AppDataGridLookupPromise>>();

function setLoadingButton(buttonName?: string) {
	currentLoadingButton.value = buttonName;
}

const tryLookup = useGridLookup(gridLookup);

onCloseTab((e) => {
	if (dirty.value && !confirm("Er zijn onopgeslagen wijzigingen. Wilt u doorgaan?")) {
		e.prevent = true;
	}
});

async function submit(formValues: T) {
	formNode.emit("pre-submit", formValues);

	if (formNode.context && Object.values(formNode.context.messages).filter((msg) => msg.blocking).length > 0) {
		currentLoadingButton.value = null;
		return;
	}

	hasActiveSubmitRequest.value = true;
	let data: T;
	if (props.onBeforeSubmit != null) {
		try {
			data = await props.onBeforeSubmit(formValues, null!);
		} catch {
			hasActiveSubmitRequest.value = false;
			currentLoadingButton.value = null;
			return;
		}
	}
	const submitMethod = props.type === "Create" ? "post" : "put";
	await submitForm({
		data,
		method: submitMethod,
		submitUrl: props.submitUrl || resource.uri,
	}).finally(() => {
		hasActiveSubmitRequest.value = false;
		currentLoadingButton.value = null;
		refreshParentGrid();
	});
}

function refreshParentGrid() {
	if (props.resourceKey.includes("row")) {
		const parentResource = props.resourceKey.split("-row")[0];
		refreshGridsOfResource(parentResource);
	}
}

const events = {
	form: {
		node: (e: FormKitNode) => {
			formNode = e;
			formNodeRef.value = e;

			formNode.on("update:property", (event) => {
				vModel.value[event.payload.name] = event.payload.value;
			});

			// Remove tabindex from dropdown buttons.
			e.on("child", onInputMounted);

			watch(
				isReady,
				async () => {
					onFormMounted();
					await new Promise((r) =>
						setTimeout(() => {
							watchOnce(vModel, () => {
								dirty.value = true;
							});
						}, 200),
					);
				},
				{ immediate: isReady.value, once: true },
			);

			return e.on("autocompleteLookup.deep", tryLookup);
		},
		submit,
	},
};

provide("formNode", formNodeRef);
// AppSchemaInput binds its native inputs straight to these, so the form model is the single source
// of truth for a field's value. This replaces a `microdiff` watch that mirrored every model change
// back onto the schema objects — see AppSchemaInput's `inputValue`.
provide("formValues", vModel);
provide("formSchema", formObjectDefinition);

defineExpose({ isReady });
</script>

<style scoped></style>
